import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Building2, CalendarClock, Users, UserRound, CalendarPlus, Loader2, RotateCcw, NotebookPen, ClipboardCheck } from "lucide-react";

const TYPE_LABEL = { visita_partner: "Visita", telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email / contatto", review_partner: "Review Partner", affiancamento_agenzia: "Affiancamento agenzia" };
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);

// Obiettivo pre-compilato per le visite di riattivazione, così il responsabile parte con il contesto.
function reactObjective(it) {
  if (!it?.riattivazione) return null;
  if (it.giorni == null)
    return "Obiettivo: attivare il cliente (mai un ordine registrato). Presentare la gamma TORRI e individuare la prima opportunità.";
  const mesi = Math.max(1, Math.round(it.giorni / 30));
  return `Obiettivo: recuperare cliente inattivo da ~${mesi} mesi${it.last_order_date ? ` (ultimo ordine ${fmtDate(it.last_order_date)})` : ""}. Verificare i motivi del calo e riattivare gli ordini.`;
}

const CLASS_STYLE = {
  A: "bg-[#FDD003] text-black border-[#FDD003]",
  B: "bg-[#F59E0B] text-white border-[#F59E0B]",
  C: "bg-slate-400 text-white border-slate-400",
  D: "bg-slate-600 text-white border-slate-600",
};

function bucketOf(it) {
  if (it.riattivazione) return { key: "riattivazione", label: "Riattivazione", order: 0, isReact: true };
  if (it.is_partner === false) return { key: "cliente", label: "Clienti", order: 6, isClient: true };
  const c = it.partner_categoria;
  if (c && CLASS_STYLE[c]) return { key: `partner_${c}`, label: `Partner · Classe ${c}`, order: { A: 1, B: 2, C: 3, D: 4 }[c], cat: c };
  return { key: "partner_nc", label: "Partner · Non profilato", order: 5 };
}

function FissaPopover({ it, onChanged, actor = "responsabile_commerciale", ownerId = null }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(it.date || todayISO());
  const [time, setTime] = useState(it.time || "");
  const [aff, setAff] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date) { toast.error("Seleziona una data"); return; }
    setBusy(true);
    try {
      if (it.riattivazione || it.virtual) {
        await api.post(`/activities`, {
          type: "visita_partner", date, time, partner_id: it.partner_id, agency_id: it.agency_id || null,
          status: "pianificato", actor, owner_id: it.owner_id || ownerId || null,
          notes: reactObjective(it) || it.motivo || null,
          affiancamento_richiesto: aff,
        });
      } else {
        await api.post(`/activities/${it.id}/schedule`, { date, time, affiancamento_richiesto: aff });
      }
      toast.success("Appuntamento fissato in agenda");
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" title="Fissa appuntamento" data-testid={`fissa-btn-${it.id}`}><CalendarPlus className="h-4 w-4 text-primary" /></Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" data-testid={`fissa-popover-${it.id}`}>
        <div className="text-sm font-semibold">Fissa appuntamento</div>
        <div><Label className="mb-1 block text-xs">Data</Label><DatePicker value={date} onChange={(v) => setDate(v)} data-testid={`fissa-date-${it.id}`} /></div>
        <div><Label className="mb-1 block text-xs">Ora (opzionale)</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid={`fissa-time-${it.id}`} /></div>
        {actor === "agenzia" && (
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-orange-300 bg-orange-50 p-2" data-testid={`fissa-aff-row-${it.id}`}>
            <Checkbox checked={aff} onCheckedChange={(v) => setAff(!!v)} data-testid={`fissa-aff-${it.id}`} />
            <Users className="h-3.5 w-3.5 text-orange-600" />
            <span className="text-[11px]">Responsabile in affiancamento</span>
          </label>
        )}
        <Button size="sm" className="w-full" onClick={submit} disabled={busy} data-testid={`fissa-confirm-${it.id}`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarPlus className="mr-2 h-4 w-4" />} Fissa in agenda
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function VisiteDaProgrammareDialog({ open, onOpenChange, items, onChanged, actor = "responsabile_commerciale", ownerId = null, onNuovaAttivita, onRegistraVisita }) {
  const groups = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const ag = it.resolved_agency_name || "Senza agenzia";
      const b = bucketOf(it);
      map[ag] = map[ag] || { agency: ag, buckets: {}, total: 0 };
      map[ag].buckets[b.key] = map[ag].buckets[b.key] || { ...b, items: [] };
      map[ag].buckets[b.key].items.push(it);
      map[ag].total += 1;
    });
    return Object.values(map)
      .map((g) => ({ ...g, bucketList: Object.values(g.buckets).sort((a, b) => a.order - b.order) }))
      .sort((a, b) => b.total - a.total);
  }, [items]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="da-programmare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Visite da programmare</DialogTitle>
          <DialogDescription>Attività consigliate dal programma di presidio, divise per profilo partner e cliente. Usa le scorciatoie per fissare un appuntamento in agenda o aggiungere un'altra azione.</DialogDescription>
        </DialogHeader>

        {groups.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground" data-testid="da-programmare-empty">Nessuna visita da programmare. Genera il programma dalle Attività Responsabile.</div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.agency} className="rounded-lg border border-border" data-testid={`da-programmare-agency-${g.agency}`}>
                <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2.5">
                  <div className="flex items-center gap-2 font-heading text-sm font-bold"><Building2 className="h-4 w-4 text-primary" /> {g.agency}</div>
                  <Badge className="bg-primary text-primary-foreground">{g.total} da programmare</Badge>
                </div>
                <div className="divide-y divide-border">
                  {g.bucketList.map((b) => (
                    <div key={b.key} className="p-3" data-testid={`da-programmare-bucket-${b.key}`}>
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
                        {b.isReact ? <RotateCcw className="h-3.5 w-3.5 text-amber-600" /> : b.isClient ? <Users className="h-3.5 w-3.5 text-muted-foreground" /> : <UserRound className="h-3.5 w-3.5 text-muted-foreground" />}
                        {b.isReact ? <Badge className="border border-amber-300 bg-amber-100 text-amber-800">Riattivazione</Badge>
                          : b.cat ? <Badge className={`border ${CLASS_STYLE[b.cat]}`}>{b.label}</Badge>
                          : <span className="text-muted-foreground">{b.label}</span>}
                        <span className="text-muted-foreground">· {b.items.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {b.items.sort((a, c) => (a.date || "").localeCompare(c.date || "")).map((it) => (
                          <div key={it.id} className="flex items-center justify-between gap-3 rounded-md bg-accent/40 px-3 py-1.5 text-sm" data-testid={`da-programmare-item-${it.id}`}>
                            <div className="min-w-0">
                              {it.partner_id ? (
                                <Link to={`/partners/${it.partner_id}`} onClick={() => onOpenChange(false)} className="font-semibold text-primary hover:underline" data-testid={`da-programmare-link-${it.id}`}>{it.partner_name || "—"}</Link>
                              ) : (
                                <span className="font-semibold">{it.partner_name || "—"}</span>
                              )}
                              <span className="ml-2 text-xs text-muted-foreground">{it.motivo || (TYPE_LABEL[it.type] || it.type)}</span>
                              {it.owner_name && <span className="ml-2 text-xs text-muted-foreground">· {it.owner_name}</span>}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              <span className="font-mono text-xs text-muted-foreground">{fmtDate(it.date)}</span>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Nuova attività" onClick={() => onNuovaAttivita?.({ kind: "partner", id: it.partner_id, name: it.partner_name })} data-testid={`dp-attivita-${it.id}`}><NotebookPen className="h-4 w-4 text-muted-foreground" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Registra visita" onClick={() => onRegistraVisita?.({ kind: "partner", id: it.partner_id, name: it.partner_name })} data-testid={`dp-visita-${it.id}`}><ClipboardCheck className="h-4 w-4 text-primary" /></Button>
                              <FissaPopover it={it} onChanged={onChanged} actor={actor} ownerId={ownerId} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
